import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerType, Region } from '@kalachand/shared';
import { apiGet, apiPatch } from '../../lib/api';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { Money } from '../../components/Money';
import { StatusPill, orderStatusTone, paymentStatusTone } from '../../components/StatusPill';
import { Button } from '../../components/Button';
import { LocationPicker, LocationPreview } from '../../components/LocationPicker';
import { Can, useHasPermission } from '../../lib/permissions';
import { CustomerCreateForm, type EditableCustomer } from './CustomerCreateForm';

interface CustomerDetail {
  id: string;
  customerCode: string;
  type: CustomerType;
  name: string;
  phone: string;
  altPhones?: string[];
  email: string | null;
  companyName: string | null;
  vatNumber: string | null;
  brn: string | null;
  contactPerson: string | null;
  nationalId: string | null;
  dateOfBirth: string | null;
  region: Region | null;
  vatExcluded: boolean;
  address: string | null;
  addressPrecise: string | null;
  city: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  notes: string | null;
  preferredChannel: string;
  preferredShop?: { id: string; code: string; name: string } | null;
  createdAt: string;
  outstandingBalance: string;
  lifetimeSpend: string;
  avgTicket: string;
  orderCount: number;
  topCategory: string | null;
  orders: Array<{
    id: string;
    number: string;
    status: string;
    paymentStatus: string;
    fulfillmentType: string;
    total: string;
    paidTotal: string;
    balanceDue: string;
    createdAt: string;
    shop: { code: string; name: string };
    lines: Array<{
      qty: number;
      product: { name: string; sku: string; category: { code: string; name: string } | null };
    }>;
  }>;
  notifications: Array<{
    id: string;
    channel: string;
    status: string;
    refType: string;
    attemptedAt: string;
  }>;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function channelEmoji(ch: string): string {
  switch (ch) {
    case 'whatsapp':
      return '💬';
    case 'email':
      return '✉';
    case 'print':
      return '🖨';
    case 'download':
      return '⬇';
    case 'sms':
      return '💭';
    default:
      return '·';
  }
}

interface CreditNoteForCustomer {
  id: string;
  number: string;
  status: 'DRAFT' | 'ISSUED' | 'APPLIED' | 'VOID';
  reason: string;
  total: string;
  remainingTotal: string;
  issuedAt: string | null;
  createdAt: string;
  invoice: { number: string } | null;
}

export function CustomerDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const customer = useQuery({
    queryKey: ['customer', id],
    queryFn: () => apiGet<CustomerDetail>(`/customers/${id}`),
  });

  // Owner-only — fetch credit notes for this customer to surface them inline.
  const canSeeCredits = useHasPermission('credit_notes.create');
  const credits = useQuery({
    queryKey: ['customer-credits', id],
    queryFn: () => apiGet<CreditNoteForCustomer[]>('/credit-notes', { customerId: id }),
    enabled: canSeeCredits && !!id,
  });

  const saveRegion = useMutation({
    mutationFn: (region: Region) => apiPatch(`/customers/${id}`, { region }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer', id] }),
  });

  const saveLocation = useMutation({
    mutationFn: (loc: { lat: number; lng: number; address: string }) =>
      apiPatch(`/customers/${id}`, {
        latitude: loc.lat,
        longitude: loc.lng,
        ...(loc.address ? { address: loc.address } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer', id] });
      setPickerOpen(false);
    },
  });

  if (customer.isLoading)
    return (
      <PageScaffold title="Customer">
        <div className="text-ink-3 text-center py-20">Loading…</div>
      </PageScaffold>
    );
  if (!customer.data)
    return (
      <PageScaffold title="Customer">
        <div className="text-ink-3 text-center py-20">Customer not found.</div>
      </PageScaffold>
    );
  const c = customer.data;
  const display = c.type === 'company' ? (c.companyName ?? c.name) : c.name;
  const since = new Date(c.createdAt);
  const sinceLabel = since.toLocaleDateString('en-MU', { month: 'short', year: 'numeric' });
  const liveOrders = c.orders.filter((o) => o.status !== 'CANCELLED');
  const lastVisitAt = liveOrders[0]?.createdAt;

  return (
    <PageScaffold
      back={{ to: '/customers', label: 'Customers' }}
      title={
        <span className="inline-flex items-center gap-3 align-middle">
          {display}
          <StatusPill tone={c.type === 'company' ? 'accent' : 'neutral'}>{c.type}</StatusPill>
          {c.vatExcluded && <StatusPill tone="info">VAT excluded</StatusPill>}
        </span>
      }
      subtitle={
        <>
          Customer <span className="kc-mono text-xs">{c.customerCode}</span> · since{' '}
          <b className="text-ink">{sinceLabel}</b>
          {c.preferredShop && (
            <>
              {' '}
              · prefers <b className="text-ink">{c.preferredShop.name}</b>
            </>
          )}
        </>
      }
      actions={
        <>
          <Button variant="ghost" disabled title="Wired in Phase 2">
            💬 WhatsApp
          </Button>
          <Button variant="ghost" disabled title="Wired in Phase 2">
            ✉ Email
          </Button>
          <Can permission="customers.edit">
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </Can>
          <Link to={`/orders/new`}>
            <Button>+ New order</Button>
          </Link>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — profile + lifetime */}
        <div className="space-y-6">
          <Card>
            <div className="flex flex-col items-start gap-4">
              <div className="w-20 h-20 rounded-full bg-accent-soft text-accent flex items-center justify-center kc-serif text-3xl shrink-0">
                {initials(display)}
              </div>
              <div className="w-full">
                <h2 className="text-lg font-medium text-ink leading-tight">{display}</h2>
                <p className="text-xs text-ink-3 mt-2">
                  {c.orderCount} order{c.orderCount === 1 ? '' : 's'}
                </p>
              </div>
              <dl className="w-full space-y-2 text-sm pt-3 border-t border-rule">
                {c.type === 'company' && (
                  <KV k="Contact person" v={c.contactPerson ?? c.name} />
                )}
                <KV k="Phone" v={<span className="kc-mono text-xs">{c.phone}</span>} />
                {(c.altPhones?.length ?? 0) > 0 && (
                  <KV
                    k="Other numbers"
                    v={<span className="kc-mono text-xs">{c.altPhones!.join(' · ')}</span>}
                  />
                )}
                {c.email && <KV k="Email" v={c.email} />}
                {c.type === 'company' && c.vatNumber && (
                  <KV k="VAT" v={<span className="kc-mono text-xs">{c.vatNumber}</span>} />
                )}
                {c.type === 'company' && c.brn && (
                  <KV k="BRN" v={<span className="kc-mono text-xs">{c.brn}</span>} />
                )}
                {c.nationalId && (
                  <KV
                    k="NIC / passport"
                    v={<span className="kc-mono text-xs">{c.nationalId}</span>}
                  />
                )}
                {c.dateOfBirth && (
                  <KV
                    k="Date of birth"
                    v={
                      <span>
                        {new Date(c.dateOfBirth).toLocaleDateString('en-MU', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                        <span className="text-ink-3 text-xs ml-1.5">
                          ({calcAge(c.dateOfBirth)})
                        </span>
                      </span>
                    }
                  />
                )}
                <KV
                  k="Region"
                  v={
                    <select
                      value={c.region ?? ''}
                      onChange={(e) =>
                        e.target.value && saveRegion.mutate(e.target.value as Region)
                      }
                      disabled={saveRegion.isPending}
                      className="bg-surface border border-rule rounded text-xs px-2 py-1 focus:outline-none focus:border-accent"
                    >
                      <option value="">— not set —</option>
                      <option value="center">Center</option>
                      <option value="north">North</option>
                      <option value="east">East</option>
                      <option value="south">South</option>
                      <option value="west">West</option>
                    </select>
                  }
                />
                {[c.address, c.city, c.country].filter(Boolean).length > 0 && (
                  <KV k="Address" v={[c.address, c.city, c.country].filter(Boolean).join(', ')} />
                )}
                <KV k="Channel" v={<span className="capitalize">{c.preferredChannel}</span>} />
              </dl>

              {/* Map pin */}
              <div className="w-full pt-3 border-t border-rule">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-ink-3">Map location</div>
                  <Button
                    type="button"
                    variant="tertiary"
                    size="xs"
                    onClick={() => setPickerOpen(true)}
                  >
                    {c.latitude != null ? 'Change pin' : 'Pin location'}
                  </Button>
                </div>
                {c.latitude != null && c.longitude != null ? (
                  <>
                    <LocationPreview lat={c.latitude} lng={c.longitude} height={140} />
                    <div className="text-[10px] kc-mono text-ink-3 mt-1">
                      {c.latitude.toFixed(6)}, {c.longitude.toFixed(6)}
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="w-full border border-dashed border-rule rounded-md py-5 text-xs text-ink-3 hover:bg-paper-2 hover:border-accent/40 transition"
                  >
                    + Pin on map for delivery routing
                  </button>
                )}
              </div>

              {c.notes && (
                <div className="w-full pt-3 border-t border-rule">
                  <div className="text-xs uppercase tracking-wider text-ink-3 mb-1.5">Notes</div>
                  <p className="text-sm text-ink whitespace-pre-line">{c.notes}</p>
                </div>
              )}
            </div>
          </Card>

          <LocationPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            title={`Pin ${display}`}
            initial={{
              lat: c.latitude,
              lng: c.longitude,
              address: [c.address, c.city].filter(Boolean).join(', '),
            }}
            onConfirm={(loc) => saveLocation.mutate(loc)}
          />

          <Card title="Lifetime">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-3">Lifetime spend</div>
              <Money value={c.lifetimeSpend} className="kc-serif text-3xl text-ink block mt-1" />
            </div>
            <dl className="mt-4 space-y-2.5 text-sm">
              <KVNum k="Orders" v={c.orderCount} />
              <KVNum k="Avg ticket" v={<Money value={c.avgTicket} />} />
              <KVNum
                k="Outstanding"
                v={
                  <Money
                    value={c.outstandingBalance}
                    className={Number(c.outstandingBalance) > 0 ? 'text-warning' : 'text-positive'}
                  />
                }
              />
              {c.topCategory && (
                <KVNum k="Top category" v={<span className="text-ink">{c.topCategory}</span>} />
              )}
              {lastVisitAt && (
                <KVNum
                  k="Last seen"
                  v={new Date(lastVisitAt).toLocaleDateString('en-MU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                />
              )}
            </dl>
          </Card>
        </div>

        {/* RIGHT — order history + comms */}
        <div className="lg:col-span-2 space-y-6">
          <Card
            title="Order history"
            subtitle={`${c.orders.length} order${c.orders.length === 1 ? '' : 's'} across all shops`}
            bodyClassName="p-0"
          >
            {c.orders.length === 0 ? (
              <div className="text-sm text-ink-3 px-6 py-10 text-center">No orders yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                      <th className="px-4 py-2 font-medium">Invoice</th>
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium">Shop</th>
                      <th className="px-3 py-2 font-medium w-full">Items</th>
                      <th className="px-3 py-2 font-medium text-right">Total</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.orders.map((o) => {
                      const itemSummary =
                        o.lines
                          .slice(0, 2)
                          .map((l) => `${l.qty}× ${l.product.name}`)
                          .join(', ') + (o.lines.length > 2 ? `, +${o.lines.length - 2} more` : '');
                      return (
                        <tr key={o.id} className="border-b border-rule-2 hover:bg-paper-2">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Link
                              to={`/orders/${o.id}`}
                              className="kc-mono text-xs text-ink hover:text-accent"
                            >
                              {o.number.startsWith('DRAFT-') ? 'DRAFT' : o.number}
                            </Link>
                          </td>
                          <td className="px-3 py-3 text-xs text-ink-3 whitespace-nowrap">
                            {new Date(o.createdAt).toLocaleDateString('en-MU', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </td>
                          <td className="px-3 py-3">
                            <StatusPill>{o.shop.code}</StatusPill>
                          </td>
                          <td className="px-3 py-3 text-xs text-ink-3 truncate max-w-[200px]">
                            {itemSummary}
                          </td>
                          <td className="px-3 py-3 text-right whitespace-nowrap">
                            <Money value={o.total} />
                          </td>
                          <td className="px-3 py-3">
                            <StatusPill tone={orderStatusTone(o.status)}>{o.status}</StatusPill>
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill tone={paymentStatusTone(o.paymentStatus)}>
                              {o.paymentStatus}
                            </StatusPill>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Credit notes — owner-only inline panel */}
          <Can permission="credit_notes.create">
            <Card
              title="Credit notes"
              subtitle="Owner-only · reverses AR after invoice issue"
              bodyClassName="p-0"
            >
              <div className="px-6 py-3 border-b border-rule flex items-center justify-between">
                <div className="text-xs text-ink-3">
                  {credits.isLoading
                    ? 'Loading…'
                    : credits.data && credits.data.length > 0
                      ? `${credits.data.length} credit note${credits.data.length === 1 ? '' : 's'}`
                      : 'No credit notes for this customer'}
                </div>
                <Link to={`/credits/new?customerId=${c.id}`}>
                  <Button size="sm" variant="secondary">
                    + Issue credit note
                  </Button>
                </Link>
              </div>
              {credits.data && credits.data.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-b border-rule">
                      <th className="px-6 py-2 font-medium">CN</th>
                      <th className="px-6 py-2 font-medium">Date</th>
                      <th className="px-6 py-2 font-medium">Against</th>
                      <th className="px-6 py-2 font-medium">Reason</th>
                      <th className="px-6 py-2 font-medium text-right">Total</th>
                      <th className="px-6 py-2 font-medium text-right">Remaining</th>
                      <th className="px-6 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {credits.data.map((cn) => (
                      <tr key={cn.id} className="border-b border-rule-2 hover:bg-paper-2">
                        <td className="px-6 py-2.5">
                          <Link
                            to={`/credits/${cn.id}`}
                            className="kc-mono text-xs text-ink hover:text-accent"
                          >
                            {cn.number}
                          </Link>
                        </td>
                        <td className="px-6 py-2.5 text-xs text-ink-3">
                          {new Date(cn.issuedAt ?? cn.createdAt).toLocaleDateString('en-MU', {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </td>
                        <td className="px-6 py-2.5 text-xs text-ink-2 kc-mono">
                          {cn.invoice?.number ?? '—'}
                        </td>
                        <td className="px-6 py-2.5 text-xs text-ink-2 capitalize">
                          {cn.reason.replace(/_/g, ' ').toLowerCase()}
                        </td>
                        <td className="px-6 py-2.5 text-right">
                          <Money value={cn.total} />
                        </td>
                        <td className="px-6 py-2.5 text-right">
                          <Money
                            value={cn.remainingTotal}
                            className={
                              Number(cn.remainingTotal) > 0
                                ? 'text-accent'
                                : 'text-ink-3 line-through'
                            }
                          />
                        </td>
                        <td className="px-6 py-2.5">
                          <StatusPill
                            tone={
                              cn.status === 'APPLIED'
                                ? 'positive'
                                : cn.status === 'ISSUED'
                                  ? 'warning'
                                  : cn.status === 'VOID'
                                    ? 'negative'
                                    : 'neutral'
                            }
                          >
                            {cn.status.toLowerCase()}
                          </StatusPill>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </Card>
          </Can>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Communications" subtitle="Notifications + invoice handoffs">
              {c.notifications.length === 0 ? (
                <div className="text-sm text-ink-3 text-center py-4">No communications yet.</div>
              ) : (
                <ul className="space-y-3.5">
                  {c.notifications.slice(0, 8).map((n) => (
                    <li key={n.id} className="flex gap-3 text-sm">
                      <span className="w-7 h-7 rounded-full bg-paper-2 border border-rule flex items-center justify-center text-base shrink-0">
                        {channelEmoji(n.channel)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-ink-3">
                          {new Date(n.attemptedAt).toLocaleString('en-MU', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        <div className="text-ink">
                          {n.refType === 'Invoice' ? 'Invoice ' : `${n.refType} · `}
                          <span className="text-ink-2 capitalize">{n.channel}</span>{' '}
                          <StatusPill
                            tone={
                              n.status === 'sent'
                                ? 'positive'
                                : n.status === 'stubbed'
                                  ? 'neutral'
                                  : n.status === 'failed'
                                    ? 'negative'
                                    : 'info'
                            }
                          >
                            {n.status}
                          </StatusPill>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="At a glance" subtitle="Behaviour signals">
              <ul className="space-y-2.5 text-sm">
                {/* Pattern-derived signals need ≥ 3 orders to mean anything —
                    "most purchased category" of 1 order is just "that one order". */}
                {c.topCategory && c.orderCount >= 3 && (
                  <li>
                    <StatusPill tone="accent">{c.topCategory}</StatusPill> · most purchased category
                  </li>
                )}
                {c.preferredShop && (
                  <li>
                    <StatusPill>{c.preferredShop.code}</StatusPill> · prefers {c.preferredShop.name}
                  </li>
                )}
                <li>
                  <StatusPill tone={c.preferredChannel === 'whatsapp' ? 'positive' : 'info'}>
                    {c.preferredChannel}
                  </StatusPill>{' '}
                  · preferred channel
                </li>
                {c.vatExcluded && (
                  <li>
                    <StatusPill tone="info">VAT excluded</StatusPill> · all invoices skip VAT
                  </li>
                )}
                {(() => {
                  // Distinguish "deposit-then-balance is normal" from "actually overdue".
                  // Overdue = order shipped (DELIVERED/COMPLETED) but balance still open.
                  // Pending = order placed, awaiting balance — expected, no chase needed.
                  const overdueOrders = c.orders.filter(
                    (o) =>
                      (o.status === 'DELIVERED' || o.status === 'COMPLETED') &&
                      Number(o.balanceDue) > 0,
                  );
                  const overdue = overdueOrders.reduce((sum, o) => sum + Number(o.balanceDue), 0);
                  const total = Number(c.outstandingBalance);
                  const pending = Math.max(0, total - overdue);
                  if (overdue > 0) {
                    return (
                      <li>
                        <StatusPill tone="warning">Overdue {overdue.toFixed(2)}</StatusPill> ·
                        follow up
                      </li>
                    );
                  }
                  if (pending > 0) {
                    return (
                      <li>
                        <StatusPill tone="info">Pending {pending.toFixed(2)}</StatusPill> · awaiting
                        balance
                      </li>
                    );
                  }
                  if (total === 0 && c.orderCount > 0) {
                    return (
                      <li>
                        <StatusPill tone="positive">Always paid</StatusPill> · no outstanding
                      </li>
                    );
                  }
                  return null;
                })()}
              </ul>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit customer — reuses the shared create/edit form. BRN + VAT stay
          locked for anyone below operations (form + backend both enforce it). */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl my-8">
            <CustomerCreateForm
              customer={c satisfies EditableCustomer}
              cancelLabel="Cancel"
              onCreated={() => {
                qc.invalidateQueries({ queryKey: ['customer', id] });
                setEditing(false);
              }}
              onCancel={() => setEditing(false)}
            />
          </div>
        </div>
      )}
    </PageScaffold>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }): JSX.Element {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <dt className="text-ink-3">{k}</dt>
      <dd className="col-span-2 text-ink">{v}</dd>
    </div>
  );
}

function KVNum({ k, v }: { k: string; v: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-ink-3">{k}</span>
      <span className="text-ink kc-mono kc-tnum">{v}</span>
    </div>
  );
}

/** Returns a human-friendly age like "32 yrs" from an ISO date string. */
function calcAge(iso: string): string {
  const dob = new Date(iso);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  if (age < 0 || age > 130) return '—';
  return `${age} yrs`;
}
